import Tooltip from '@material-ui/core/Tooltip';
import { makeStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core/styles';
import BatteryStdIcon from '@material-ui/icons/BatteryStd';
import SyncIcon from '@material-ui/icons/Sync';
import SyncProblemIcon from '@material-ui/icons/SyncProblem';
import SignalCellularAltIcon from '@material-ui/icons/SignalCellularAlt';
import React, { FunctionComponent, memo } from 'react';
import NoTargetIcon from '@material-ui/icons/DeviceHub';
import { chartColor } from '../../../Dashboard/provider';
import { IconProps } from './types';
import {
  FlagOutlined,
  Warning,
  DeveloperMode,
  InfoOutlined,
  RemoveCircleOutline,
} from '@material-ui/icons';
import { StatusCodeInfo } from '../../types';
import { Chip } from '@material-ui/core';
import { ListDecoratedMedisanteDevice } from '../../../Devices/types';
const useStyles = makeStyles((theme: Theme) => ({
  icon: {
    marginRight: theme.spacing(1),
    fontSize: '1.43rem',
  },
  normalFont: {
    fontStyle: 'normal',
  },
}));

export const BehindSyncingSchedule: FunctionComponent<IconProps> = memo(() => {
  const classes = useStyles();

  return (
    <Tooltip
      title="This device is behind on its current syncing schedule. It may retry syncing depending on its settings."
      className={classes.icon}
    >
      <SyncProblemIcon color="error" />
    </Tooltip>
  );
});

export const OKBattery: FunctionComponent<IconProps> = memo(({ device }) => {
  const classes = useStyles();
  let batteryValue = '';
  if (
    device.batteryVoltage !== undefined &&
    device.batteryVoltageHealth === 'ok'
  ) {
    batteryValue = `Last received battery voltage: ${device.batteryVoltage} V.`;
  } else {
    return <></>;
  }

  if (device.batteryVoltageAverageHealth) {
    batteryValue += ` Average battery voltage health is ${device.batteryVoltageAverageHealth}.`;
  }
  if (device.batteryVoltageAverage) {
    batteryValue += ` Average battery voltage: ${device.batteryVoltageAverage} V.`;
  }

  return (
    <Tooltip
      title={`This device's battery voltage is ok. ${batteryValue}`}
      className={classes.icon}
    >
      <BatteryStdIcon style={{ color: chartColor('ok') }} />
    </Tooltip>
  );
});

export const MinBattery: FunctionComponent<IconProps> = memo(({ device }) => {
  const classes = useStyles();
  let batteryValue = '';
  if (device.batteryVoltage !== undefined) {
    batteryValue = `Last received battery voltage: ${device.batteryVoltage} V`;
  }
  if (device.batteryVoltageAverageHealth) {
    batteryValue += ` Average battery voltage health is ${device.batteryVoltageAverageHealth}.`;
  }
  if (device.batteryVoltageAverage) {
    batteryValue += ` Average battery voltage: ${device.batteryVoltageAverage} V.`;
  }
  return (
    <Tooltip
      title={`This device's battery voltage is critically low. Please change the batteries now. ${batteryValue}`}
      className={classes.icon}
    >
      <BatteryStdIcon
        style={{
          color: chartColor('critical'),
        }}
      />
    </Tooltip>
  );
});

export const OKSignalStrength: FunctionComponent<IconProps> = memo(
  ({ device }) => {
    const classes = useStyles();
    let signalValue = '';
    if (
      device.signalStrength !== undefined &&
      device.signalStrengthHealth === 'ok'
    ) {
      signalValue = `Last received signal strength: ${device.signalStrength} dBm.`;
    } else {
      return <></>;
    }

    if (device.signalStrengthAverageHealth) {
      signalValue += ` Average signal strength health is ${device.signalStrengthAverageHealth}.`;
    }
    if (device.signalStrengthAverage) {
      signalValue += ` Average signal strength: ${device.signalStrengthAverage} dBm.`;
    }

    return (
      <Tooltip
        title={`This device's signal strength is ok. ${signalValue}`}
        className={classes.icon}
      >
        <SignalCellularAltIcon
          style={{
            color: chartColor('ok'),
          }}
        />
      </Tooltip>
    );
  }
);

export const MinSignalStrength: FunctionComponent<IconProps> = memo(
  ({ device }) => {
    const classes = useStyles();
    let signalValue = '';
    if (device.signalStrength !== undefined) {
      signalValue = `Last received signal strength: ${device.signalStrength} dBm.`;
    }
    if (device.signalStrengthAverageHealth) {
      signalValue += ` Average signal strength health is ${device.signalStrengthAverageHealth}.`;
    }
    if (device.signalStrengthAverage) {
      signalValue += ` Average signal strength: ${device.signalStrengthAverage} dBm.`;
    }
    return (
      <Tooltip
        title={`This device's signal strength is critically low. ${signalValue}`}
        className={classes.icon}
      >
        <SignalCellularAltIcon
          style={{
            color: chartColor('critical'),
          }}
        />
      </Tooltip>
    );
  }
);

export const LowBattery: FunctionComponent<IconProps> = memo(({ device }) => {
  const classes = useStyles();
  let batteryValue = '';
  if (device.batteryVoltage !== undefined) {
    batteryValue = `Last received battery voltage: ${device.batteryVoltage} V.`;
  }
  if (device.batteryVoltageAverageHealth) {
    batteryValue += ` Average battery voltage health is ${device.batteryVoltageAverageHealth}.`;
  }
  if (device.batteryVoltageAverage) {
    batteryValue += ` Average battery voltage: ${device.batteryVoltageAverage} V.`;
  }

  return (
    <Tooltip
      title={`This device's battery voltage is low. Please change the batteries soon. ${batteryValue}`}
      className={classes.icon}
    >
      <BatteryStdIcon
        style={{
          color: chartColor('warning'),
        }}
      />
    </Tooltip>
  );
});

export const LowSignalStrength: FunctionComponent<IconProps> = memo(
  ({ device }) => {
    const classes = useStyles();
    let signalValue = '';
    if (device.signalStrength !== undefined) {
      signalValue = `Last received signal strength: ${device.signalStrength} dBm.`;
    }
    if (device.signalStrengthAverageHealth) {
      signalValue += ` Average signal strength health is ${device.signalStrengthAverageHealth}.`;
    }
    if (device.signalStrengthAverage) {
      signalValue += ` Average signal strength: ${device.signalStrengthAverage} dBm.`;
    }
    return (
      <Tooltip
        title={`This device's signal strength is low. ${signalValue}`}
        className={classes.icon}
      >
        <SignalCellularAltIcon
          style={{
            color: chartColor('warning'),
          }}
        />
      </Tooltip>
    );
  }
);

export const PendingChanges: FunctionComponent<IconProps> = memo(
  ({ device }) => {
    const classes = useStyles();

    const pendingChangesTooltip = () => {
      let msg =
        'This device has pending changes which will be applied when it performs its next sync.';
      if (device.currentHeartbeatTimerDuration) {
        msg += ` Currently, it should sync every ${device.currentHeartbeatTimerDuration} minutes.`;
      }
      return msg;
    };

    return (
      <Tooltip title={pendingChangesTooltip()} className={classes.icon}>
        <SyncIcon color="inherit" />
      </Tooltip>
    );
  }
);

export const NoTargetSystem = memo(() => {
  const classes = useStyles();

  return (
    <Tooltip
      title="Webhook (for any measurements) not set for this device."
      className={classes.icon}
    >
      <NoTargetIcon color="error" />
    </Tooltip>
  );
});

export const ConfigurationWarning = memo(() => {
  const classes = useStyles();

  return (
    <Tooltip
      title="Configuration Warning - Devices and/or measurements may not be supported wholly or partly"
      className={classes.icon}
    >
      <Warning color="error" />
    </Tooltip>
  );
});

export const TestDeviceInfo = memo(() => {
  const classes = useStyles();

  return (
    <Tooltip title="This device is a test device." className={classes.icon}>
      <DeveloperMode color="inherit" />
    </Tooltip>
  );
});

interface FlagStatusProps {
  statuses: string[];
}

export const FlagStatus: FunctionComponent<FlagStatusProps> = memo(
  ({ statuses }) => {
    const classes = useStyles();
    const statusTexts = statuses.map((status) => {
      return status
        .split('/')
        .map((s) => capitalize(s))
        .join('/');
    });

    return (
      <Tooltip
        title={
          <React.Fragment>
            {statusTexts.map((s) => (
              <span key={s}>
                {s}
                <br />
              </span>
            ))}
          </React.Fragment>
        }
        className={classes.icon}
      >
        <FlagOutlined color="primary" />
      </Tooltip>
    );
  }
);

function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const MutedDevice = memo(() => {
  const classes = useStyles();

  return (
    <Tooltip title="This device has muted statuses." className={classes.icon}>
      <RemoveCircleOutline color="inherit" />
    </Tooltip>
  );
});

interface LastStatusCodeProps {
  device: ListDecoratedMedisanteDevice;
}

export const LastStatusCode: FunctionComponent<LastStatusCodeProps> = memo(
  ({ device }) => {
    const classes = useStyles();
    const info = (StatusCodeInfo as any)[device.model];
    const found = info
      ? info.find((i: any) => i.key === device.lastStatusCode)
      : false;
    return found ? (
      <Tooltip title={found.value} className={classes.normalFont}>
        <Chip
          label={device.lastStatusCode}
          deleteIcon={<InfoOutlined />}
          onDelete={() => {
            /* needed for show icon */
          }}
        />
      </Tooltip>
    ) : (
      <Chip label={device.lastStatusCode} />
    );
  }
);
